/**
 * Centralized date formatting constants and utilities
 */

// Date format constants
export const DATE_FORMATS = {
  DISPLAY: 'MM/dd/yyyy',           // For displaying dates in UI (Angular DatePipe tokens)
  INPUT: 'yyyy-MM-dd',             // For HTML date inputs (ISO format required, Angular DatePipe tokens)
  INPUT_DISPLAY: 'MM/dd/yyyy',     // For text inputs that show MM/dd/yyyy
  API: 'MM/dd/yyyy',               // For API communication
  TOOLTIP: 'MMM dd, yyyy',         // For detailed tooltips
  TIMESHEET_KEY: 'MM-DD-YYYY',     // For timesheet calendar date keys (moment.js format)
} as const;

// Locale constant
export const APP_LOCALE = 'en-US' as const;

/**
 * Simple date formatting utilities using Angular's built-in DatePipe
 */
export class DateFormatConstants {

  /**
   * Standard display format for all dates across the app
   */
  static readonly DISPLAY_FORMAT = DATE_FORMATS.DISPLAY;

  /**
   * HTML input format for date fields (ISO format for native date inputs)
   */
  static readonly INPUT_FORMAT = DATE_FORMATS.INPUT;

  /**
   * Text input format for date fields that display MM/dd/yyyy
   */
  static readonly INPUT_DISPLAY_FORMAT = DATE_FORMATS.INPUT_DISPLAY;

  /**
   * API format for date submission
   */
  static readonly API_FORMAT = DATE_FORMATS.API;

  /**
   * Tooltip format for detailed tooltips
   */
  static readonly TOOLTIP_FORMAT = DATE_FORMATS.TOOLTIP;

  /**
   * Timesheet calendar date key format (used for hours/comments mapping)
   */
  static readonly TIMESHEET_KEY_FORMAT = DATE_FORMATS.TIMESHEET_KEY;

  /**
   * Locale for consistent date formatting
   */
  static readonly LOCALE = APP_LOCALE;

  /**
   * Complete format string for Angular DatePipe with locale (DEPRECATED - causes display issues)
   * @deprecated Use DISPLAY_FORMAT with separate LOCALE instead
   */
  static readonly DISPLAY_WITH_LOCALE = `${DATE_FORMATS.DISPLAY}:${APP_LOCALE}`;

  /**
   * Tooltip format with locale (DEPRECATED - causes display issues)
   * @deprecated Use TOOLTIP_FORMAT with separate LOCALE instead
   */
  static readonly TOOLTIP_WITH_LOCALE = `${DATE_FORMATS.TOOLTIP}:${APP_LOCALE}`;

  /**
   * Input display format with locale for text inputs (DEPRECATED - causes display issues)
   * @deprecated Use INPUT_DISPLAY_FORMAT with separate LOCALE instead
   */
  static readonly INPUT_DISPLAY_WITH_LOCALE = `${DATE_FORMATS.INPUT_DISPLAY}:${APP_LOCALE}`;
}

/**
 * Material Date Picker format configuration
 * Centralized here to maintain single source of truth
 */
export const MATERIAL_DATE_FORMATS = {
  parse: {
    dateInput: DATE_FORMATS.DISPLAY,
  },
  display: {
    dateInput: DATE_FORMATS.DISPLAY,
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};